import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TableSortLabel
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import * as Icons from '@mui/icons-material';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';

function AppendixAdmin() {
  const { t } = useAdminLanguage();
  const [appendices, setAppendices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    fetchAppendices();
  }, [language]);

  const fetchAppendices = async () => {
    try {
      const response = await fetch(`/api/appendices?lang=${language}`);
      const data = await response.json();
      
      // Liste des versions supportées (qui ont un composant disponible)
      const supportedVersions = ['PhotoDoc', 'Rights', 'NonReleaseBody', 'CensentSearch', 'WrittenStatement', 'GeneralAttestation', 'AttorneyWaiver'];
      
      const localFallbacks = [
        { version: 'PhotoDoc', title: 'Dossier photographique', icon: 'CameraAlt', show: true },
        { version: 'Rights', title: 'Droits Salduz', icon: 'GppMaybe', show: true },
        { version: 'NonReleaseBody', title: 'Avis de non disposition du corps', icon: 'ReportProblem', show: true },
        { version: 'CensentSearch', title: 'Visite domiciliaire', icon: 'HomeWork', show: true },
        { version: 'WrittenStatement', title: 'Déclaration écrite', icon: 'Edit', show: true },
        { version: 'GeneralAttestation', title: 'Attestation générale', icon: 'Assignment', show: true },
        { version: 'AttorneyWaiver', title: 'Renonciation à un avocat', icon: 'Gavel', show: true }
      ];

      // Filtrer les données de l'API pour n'inclure que les versions supportées
      const filteredData = data.filter(item => supportedVersions.includes(item.version));
      
      const merged = [...filteredData];
      localFallbacks.forEach(fallback => {
        const exists = merged.some(item => item.version === fallback.version);
        if (!exists) {
          merged.push(fallback);
        }
      });

      setAppendices(merged);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des annexes');
      setLoading(false);
    }
  };

  const toggleShow = async (version) => {
    try {
      setLoading(true);
      await fetch(`/api/appendices/toggle-show/${version}?lang=${language}`, {
        method: 'PUT',
      });
      setSuccess('Statut de l\'annexe modifié avec succès');
      fetchAppendices();
    } catch (err) {
      setError('Erreur lors de la modification du statut de l\'annexe');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'icon', label: t('Icône'), sortable: false },
    { id: 'title', label: t('Titre'), sortable: true },
    { id: 'status', label: t('Statut'), sortable: true },
  ]), [t]);

  const getComparableValue = (appendix, property) => {
    switch (property) {
      case 'title':
      case 'version':
        return appendix[property]?.toString().toLowerCase() || '';
      case 'status':
        return appendix.show ? 0 : 1;
      default:
        return appendix[property];
    }
  };

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(appendices, {
    defaultOrderBy: 'title',
    getComparableValue,
  });

  const renderIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    if (IconComponent) {
      return <IconComponent />;
    }
    // Icône de secours si l'API renvoie un nom inconnu
    return <Icons.Description />;
  };

  const renderCellContent = (appendix, columnId) => {
    switch (columnId) {
      case 'icon':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {renderIcon(appendix.icon)}
          </Box>
        );
      case 'status':
        return appendix.show ? (
          <Chip icon={<CheckCircleIcon />} label={t('Visible')} color="success" size="small" />
        ) : (
          <Chip icon={<BlockIcon />} label={t('Caché')} color="error" size="small" />
        );
      default:
        return appendix[columnId];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des Annexes')}
          </Typography>

          <Info type="appendix" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des annexes')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((appendix) => (
                    <TableRow key={appendix.version}>
                      {columns.map((column) => (
                        <TableCell key={`${appendix.version}-${column.id}`}>
                          {renderCellContent(appendix, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Button
                          onClick={() => toggleShow(appendix.version)}
                          color={appendix.show ? "error" : "success"}
                          size="small"
                          disabled={loading}
                          startIcon={appendix.show ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                        >
                          {appendix.show ? t('Cacher') : t('Afficher')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default AppendixAdmin;

